import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Linking, TouchableOpacity } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { Text, Card, Button, IconButton, Divider, List, Dialog, Portal, TextInput, RadioButton, Chip } from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const ClienteDetalleScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { cliente } = route.params;

  const [ventasCliente, setVentasCliente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editedCliente, setEditedCliente] = useState({ ...cliente });
  const [expandedVenta, setExpandedVenta] = useState(null);
  const [estadisticas, setEstadisticas] = useState({ total_ventas: 0, total_compras: 0, promedio: 0 });
  const [filtroEstado, setFiltroEstado] = useState('');

  // Definir handleDelete primero
  const handleDelete = useCallback(async () => {
    try {
      // Llamada real a la API para eliminar el cliente
      console.log(`Eliminando cliente ID: ${cliente.id}`);
      await api.deleteCliente(cliente.id);
      
      Alert.alert(
        'Cliente Eliminado',
        `El cliente ${cliente.nombre} ha sido eliminado exitosamente`,
        [
          { 
            text: 'OK', 
            onPress: () => navigation.goBack()
          }
        ]
      );
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      Alert.alert('Error', `No se pudo eliminar el cliente: ${error.message || 'Error desconocido'}`);
    }
  }, [cliente.id, cliente.nombre, navigation]);

  // Usar useCallback para evitar recrear la función en cada renderizado
  const handleDeleteConfirmation = useCallback(() => {
    Alert.alert(
      'Eliminar Cliente',
      `¿Estás seguro de que deseas eliminar a ${cliente.nombre}?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Eliminar', style: 'destructive', onPress: handleDelete }
      ]
    );
  }, [cliente.nombre, handleDelete]);

  // Usar useCallback para loadVentasCliente para evitar recreaciones innecesarias
  const loadVentasCliente = useCallback(async () => {
    try {
      setLoading(true);
      const params = filtroEstado ? `?estado=${filtroEstado}` : '';
      const response = await api.get(`/clientes/${cliente.id}/ventas${params}`);

      if (response && response.ventas) {
        setVentasCliente(response.ventas);
        setEstadisticas(response.estadisticas || {});
      } else if (Array.isArray(response)) {
        // Compatibilidad si el backend devuelve array directo
        setVentasCliente(response);
      } else {
        setVentasCliente([]);
      }
    } catch (error) {
      console.error('Error al cargar ventas del cliente:', error);
      setVentasCliente([]);
    } finally {
      setLoading(false);
    }
  }, [cliente.id, filtroEstado]);
  
  useEffect(() => {
    loadVentasCliente();
    
    navigation.setOptions({
      title: cliente.nombre,
      headerRight: () => (
        <View style={{ flexDirection: 'row' }}>
          <IconButton
            icon="pencil"
            size={24}
            color="#007bff"
            onPress={() => setEditDialogVisible(true)}
          />
          <IconButton
            icon="delete"
            size={24}
            color="#dc3545"
            onPress={handleDeleteConfirmation}
          />
        </View>
      ),
    });
  }, [navigation, cliente, handleDeleteConfirmation, loadVentasCliente]);

  // Tanto handleDelete como handleDeleteConfirmation se movieron arriba usando useCallback

  const handleEditCliente = async () => {
    try {
      // Validar campos
      if (!editedCliente.nombre.trim()) {
        Alert.alert('Error', 'El nombre es obligatorio');
        return;
      }
      
      if (!editedCliente.telefono.trim()) {
        Alert.alert('Error', 'El teléfono es obligatorio');
        return;
      }
      
      // Llamada real a la API para actualizar el cliente
      console.log('Enviando datos actualizados a la API:', editedCliente);
      await api.updateCliente(cliente.id, editedCliente);
      
      setEditDialogVisible(false);
      
      // Actualizar la navegación con el cliente actualizado
      navigation.setParams({ cliente: editedCliente });
      
      Alert.alert(
        'Cliente Actualizado', 
        'Los datos del cliente han sido actualizados exitosamente',
        [
          { 
            text: 'OK', 
            onPress: () => {
              // Recargar la pantalla de clientes al volver
              const prevScreen = navigation.getState().routes.find(r => r.name === 'Clientes');
              if (prevScreen && prevScreen.params && typeof prevScreen.params.onGoBack === 'function') {
                prevScreen.params.onGoBack();
              }
            } 
          }
        ]
      );
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      Alert.alert('Error', `No se pudo actualizar el cliente: ${error.message || 'Error desconocido'}`);
    }
  };

  const handleNuevaVenta = () => {
    // Navegar al tab de Ventas y luego a NuevaVenta
    navigation.navigate('Ventas', {
      screen: 'NuevaVenta',
      params: { cliente }
    });
  };

  const handleOpenMaps = () => {
    if (cliente.latitud && cliente.longitud) {
      const url = `https://www.google.com/maps/search/?api=1&query=${cliente.latitud},${cliente.longitud}`;
      Linking.openURL(url).catch(err => {
        console.error('Error al abrir Google Maps:', err);
        Alert.alert('Error', 'No se pudo abrir Google Maps');
      });
    } else {
      Alert.alert('Ubicación no disponible', 'Este cliente no tiene coordenadas GPS registradas');
    }
  };

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.infoCard}>
        <Card.Content>
          <View style={styles.clienteHeader}>
            <View style={styles.avatarContainer}>
              <Text style={styles.avatarText}>
                {cliente.nombre.split(' ').map(n => n[0]).join('').toUpperCase()}
              </Text>
            </View>
            <View style={styles.clienteInfo}>
              <Text style={styles.clienteName}>{cliente.nombre}</Text>
            </View>
          </View>
          
          <Divider style={styles.divider} />
          
          <List.Item
            title="Teléfono"
            description={cliente.telefono}
            left={props => <List.Icon {...props} icon="phone" />}
            style={styles.listItem}
          />
          
          <List.Item
            title="Dirección"
            description={cliente.direccion}
            left={props => <List.Icon {...props} icon="map-marker" />}
            style={styles.listItem}
          />

          {cliente.latitud && cliente.longitud && (
            <TouchableOpacity onPress={handleOpenMaps}>
              <List.Item
                title="Ubicación GPS"
                description={`Lat: ${parseFloat(cliente.latitud).toFixed(6)}, Long: ${parseFloat(cliente.longitud).toFixed(6)}`}
                left={props => <List.Icon {...props} icon="map-marker-radius" color="#007bff" />}
                right={props => <List.Icon {...props} icon="open-in-new" color="#007bff" />}
                style={styles.listItem}
              />
            </TouchableOpacity>
          )}

          {cliente.ciudad && (
            <List.Item
              title="Ciudad"
              description={cliente.ciudad}
              left={props => <List.Icon {...props} icon="city" />}
              style={styles.listItem}
            />
          )}
          
          <List.Item
            title="Correo Electrónico"
            description={cliente.email || 'No especificado'}
            left={props => <List.Icon {...props} icon="email" />}
            style={styles.listItem}
          />
          
          {cliente.identificacion && (
            <List.Item
              title={`${cliente.tipo_identificacion || 'Identificación'}`}
              description={cliente.identificacion}
              left={props => <List.Icon {...props} icon="card-account-details" />}
              style={styles.listItem}
            />
          )}
          
          {cliente.notas && (
            <List.Item
              title="Notas"
              description={cliente.notas}
              left={props => <List.Icon {...props} icon="note-text" />}
              style={styles.listItem}
            />
          )}

          <Divider style={styles.divider} />

          <View style={styles.creditSection}>
            <View style={styles.creditHeader}>
              <Text style={styles.creditTitle}>Tipo de Pago</Text>
              <Chip
                style={{ backgroundColor: cliente.tipo_pago === 'credito' ? '#fff3cd' : '#d4edda' }}
                textStyle={{ color: cliente.tipo_pago === 'credito' ? '#856404' : '#155724', fontWeight: '700' }}
              >
                {cliente.tipo_pago === 'credito' ? 'Crédito' : 'Contado'}
              </Chip>
            </View>
            {cliente.tipo_pago === 'credito' && (
              <View style={styles.creditDetails}>
                {cliente.nombre_negocio ? (
                  <List.Item
                    title="Negocio"
                    description={cliente.nombre_negocio}
                    left={props => <List.Icon {...props} icon="store" />}
                    style={styles.listItem}
                  />
                ) : null}
                <List.Item
                  title="Límite de Crédito"
                  description={`L. ${parseFloat(cliente.limite_credito || 0).toFixed(2)}`}
                  left={props => <List.Icon {...props} icon="credit-card" />}
                  style={styles.listItem}
                />
                <List.Item
                  title="Días de Crédito"
                  description={`${cliente.dias_credito || 0} días`}
                  left={props => <List.Icon {...props} icon="calendar-clock" />}
                  style={styles.listItem}
                />
              </View>
            )}
          </View>
        </Card.Content>
      </Card>
      
      <View style={styles.actionsContainer}>
        <Button
          mode="contained"
          icon="cart"
          onPress={handleNuevaVenta}
          style={styles.actionButton}
        >
          Nueva Venta
        </Button>
        
        <Button
          mode="outlined"
          icon="map"
          onPress={() => navigation.navigate('Rutas')}
          style={styles.actionButton}
        >
          Añadir a Ruta
        </Button>
      </View>
      
      <Card style={styles.historialCard}>
        <Card.Title title="Historial de Compras" />
        <Card.Content>
          {/* Estadísticas */}
          {estadisticas.total_ventas > 0 && (
            <View style={styles.statsRow}>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>{estadisticas.total_ventas}</Text>
                <Text style={styles.statLabel}>Compras</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: '#28a745' }]}>L. {parseFloat(estadisticas.total_compras || 0).toFixed(2)}</Text>
                <Text style={styles.statLabel}>Total</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>L. {parseFloat(estadisticas.promedio || 0).toFixed(2)}</Text>
                <Text style={styles.statLabel}>Promedio</Text>
              </View>
            </View>
          )}

          {/* Filtros de estado */}
          <View style={styles.filtrosRow}>
            {[
              { id: '', label: 'Todas' },
              { id: 'completada', label: 'Completadas' },
              { id: 'pendiente', label: 'Pendientes' },
              { id: 'cancelada', label: 'Canceladas' },
            ].map(f => (
              <Chip
                key={f.id}
                selected={filtroEstado === f.id}
                onPress={() => setFiltroEstado(f.id)}
                style={[styles.filtroChip, filtroEstado === f.id && styles.filtroActivo]}
                textStyle={filtroEstado === f.id ? { color: '#fff', fontSize: 11 } : { fontSize: 11 }}
              >
                {f.label}
              </Chip>
            ))}
          </View>

          {loading ? (
            <Text style={styles.loadingText}>Cargando ventas...</Text>
          ) : ventasCliente.length > 0 ? (
            ventasCliente.map((venta) => (
              <View key={venta.id}>
                <List.Item
                  title={`Venta #${venta.id}`}
                  description={`${new Date(venta.fecha).toLocaleDateString('es-HN')} - ${venta.estado || 'pendiente'} - ${venta.metodo_pago || 'efectivo'}`}
                  right={() => (
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text style={styles.ventaTotal}>L. {parseFloat(venta.total || 0).toFixed(2)}</Text>
                      <Text style={{ fontSize: 11, color: '#999' }}>{venta.total_items || (venta.items || []).length} items</Text>
                    </View>
                  )}
                  left={() => <Ionicons name={expandedVenta === venta.id ? 'chevron-down' : 'chevron-forward'} size={18} color="#999" style={{ alignSelf: 'center' }} />}
                  onPress={() => setExpandedVenta(expandedVenta === venta.id ? null : venta.id)}
                  style={styles.ventaItem}
                />
                {expandedVenta === venta.id && venta.items && venta.items.length > 0 && (
                  <View style={styles.ventaDetalles}>
                    {venta.items.map((item, idx) => (
                      <View key={idx} style={styles.detalleItem}>
                        <Text style={styles.detalleNombre}>{item.producto_nombre || 'Producto'}</Text>
                        <Text style={styles.detalleCant}>{item.cantidad} {item.unidad_nombre || 'Und'} x L.{parseFloat(item.precio_unitario || 0).toFixed(2)}</Text>
                        <Text style={styles.detalleSubtotal}>L. {parseFloat(item.subtotal || 0).toFixed(2)}</Text>
                      </View>
                    ))}
                    <Button compact mode="text" onPress={() => navigation.navigate('Ventas', { screen: 'DetalleVenta', params: { ventaId: venta.id } })} style={{ alignSelf: 'flex-end' }}>
                      Ver detalle completo
                    </Button>
                  </View>
                )}
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No hay ventas registradas para este cliente</Text>
          )}
        </Card.Content>
      </Card>
      
      <Portal>
        <Dialog visible={editDialogVisible} onDismiss={() => setEditDialogVisible(false)}>
          <Dialog.Title>Editar Cliente</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nombre"
              value={editedCliente.nombre}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, nombre: text })}
              style={styles.dialogInput}
            />
            
            <TextInput
              label="Teléfono"
              value={editedCliente.telefono}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, telefono: text })}
              style={styles.dialogInput}
              keyboardType="phone-pad"
            />
            
            <TextInput
              label="Dirección"
              value={editedCliente.direccion}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, direccion: text })}
              style={styles.dialogInput}
              multiline
            />

            <View style={styles.formRow}>
              <View style={styles.formColumn}>
                <TextInput
                  label="Latitud"
                  value={editedCliente.latitud ? editedCliente.latitud.toString() : ''}
                  onChangeText={(text) => setEditedCliente({ ...editedCliente, latitud: text ? parseFloat(text) : null })}
                  style={styles.dialogInput}
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.formColumn}>
                <TextInput
                  label="Longitud"
                  value={editedCliente.longitud ? editedCliente.longitud.toString() : ''}
                  onChangeText={(text) => setEditedCliente({ ...editedCliente, longitud: text ? parseFloat(text) : null })}
                  style={styles.dialogInput}
                  keyboardType="decimal-pad"
                />
              </View>
            </View>

            <TextInput
              label="Ciudad"
              value={editedCliente.ciudad || ''}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, ciudad: text })}
              style={styles.dialogInput}
            />
            
            <TextInput
              label="Correo Electrónico"
              value={editedCliente.email}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, email: text })}
              style={styles.dialogInput}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            
            <View style={styles.formRow}>
              <View style={styles.formColumn}>
                <TextInput
                  label="Identificación"
                  value={editedCliente.identificacion || ''}
                  onChangeText={(text) => setEditedCliente({ ...editedCliente, identificacion: text })}
                  style={styles.dialogInput}
                />
              </View>
              <View style={styles.formColumn}>
                <Text style={styles.selectLabel}>Tipo de ID</Text>
                <Picker
                  selectedValue={editedCliente.tipo_identificacion || 'Identidad'}
                  onValueChange={(itemValue) =>
                    setEditedCliente({ ...editedCliente, tipo_identificacion: itemValue })
                  }
                  style={styles.picker}
                >
                  <Picker.Item label="Identidad" value="Identidad" />
                  <Picker.Item label="RTN" value="RTN" />
                  <Picker.Item label="Pasaporte" value="Pasaporte" />
                </Picker>
              </View>
            </View>
            
            <Divider style={{ marginVertical: 10 }} />
            <Text style={{ fontWeight: '700', marginBottom: 6 }}>Tipo de Pago</Text>
            <RadioButton.Group
              onValueChange={value => setEditedCliente({ ...editedCliente, tipo_pago: value })}
              value={editedCliente.tipo_pago || 'contado'}
            >
              <View style={{ flexDirection: 'row' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 16 }}>
                  <RadioButton value="contado" />
                  <Text>Contado</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <RadioButton value="credito" />
                  <Text>Crédito</Text>
                </View>
              </View>
            </RadioButton.Group>
            {(editedCliente.tipo_pago === 'credito') && (
              <>
                <TextInput
                  label="Nombre del Negocio"
                  value={editedCliente.nombre_negocio || ''}
                  onChangeText={(text) => setEditedCliente({ ...editedCliente, nombre_negocio: text })}
                  style={styles.dialogInput}
                />
                <TextInput
                  label="Límite de Crédito (L.)"
                  value={String(editedCliente.limite_credito || '')}
                  onChangeText={(text) => setEditedCliente({ ...editedCliente, limite_credito: text })}
                  style={styles.dialogInput}
                  keyboardType="decimal-pad"
                />
                <Text style={{ fontWeight: '600', marginBottom: 6 }}>Días de Crédito</Text>
                <RadioButton.Group
                  onValueChange={value => setEditedCliente({ ...editedCliente, dias_credito: parseInt(value) })}
                  value={String(editedCliente.dias_credito || 8)}
                >
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {['8', '15', '22', '30'].map(d => (
                      <View key={d} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
                        <RadioButton value={d} />
                        <Text>{d}d</Text>
                      </View>
                    ))}
                  </View>
                </RadioButton.Group>
              </>
            )}

            <TextInput
              label="Notas"
              value={editedCliente.notas || ''}
              onChangeText={(text) => setEditedCliente({ ...editedCliente, notas: text })}
              style={styles.dialogInput}
              multiline
              numberOfLines={3}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setEditDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleEditCliente}>Guardar</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 8,
  },
  infoCard: {
    marginBottom: 8,
    elevation: 2,
  },
  clienteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatarContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#007bff',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  formRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  formColumn: {
    flex: 1,
    marginHorizontal: 5,
  },
  selectLabel: {
    fontSize: 12,
    color: '#6b6b6b',
    marginBottom: 5,
  },
  picker: {
    height: 50,
    backgroundColor: '#f0f0f0',
    borderRadius: 5,
  },
  avatarText: {
    color: 'white',
    fontSize: 24,
    fontWeight: 'bold',
  },
  clienteInfo: {
    flex: 1,
  },
  clienteName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  divider: {
    marginVertical: 16,
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 8,
  },
  actionButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  historialCard: {
    marginBottom: 16,
    elevation: 2,
  },
  ventaItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  ventaTotal: {
    fontWeight: 'bold',
    color: '#28a745',
  },
  emptyText: {
    textAlign: 'center',
    fontStyle: 'italic',
    color: '#666',
    marginVertical: 20,
  },
  loadingText: {
    textAlign: 'center',
    color: '#666',
    marginVertical: 20,
  },
  dialogInput: {
    marginBottom: 12,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0066cc',
  },
  statLabel: {
    fontSize: 11,
    color: '#888',
    marginTop: 2,
  },
  filtrosRow: {
    flexDirection: 'row',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  filtroChip: {
    marginRight: 6,
    marginBottom: 4,
    backgroundColor: '#e9ecef',
    height: 28,
  },
  filtroActivo: {
    backgroundColor: '#0066cc',
  },
  ventaDetalles: {
    backgroundColor: '#f8f9fa',
    marginLeft: 24,
    marginRight: 8,
    marginBottom: 8,
    padding: 10,
    borderRadius: 6,
  },
  detalleItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  detalleNombre: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  detalleCant: {
    fontSize: 12,
    color: '#888',
    marginHorizontal: 8,
  },
  detalleSubtotal: {
    fontSize: 13,
    fontWeight: '600',
    color: '#0066cc',
  },
  creditSection: {
    marginTop: 8,
  },
  creditHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  creditTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
  },
  creditDetails: {
    marginTop: 4,
  },
});

export default ClienteDetalleScreen;
